import mixpanel from 'mixpanel-browser';

const token = process.env.REACT_APP_MIXPANEL_TOKEN;
const isEnabled = process.env.REACT_APP_MIXPANEL_ENABLED === 'true';

let initialized = false;

export const MIXPANEL_EVENTS = {
  PAGE_VIEWED: 'Page Viewed',
  USER_IDENTIFIED: 'User Identified',
  LISTING_VIEWED: 'Listing Viewed',
  LISTING_PAGE_DURATION: 'Listing Page Duration',
  HOOK_VIDEO_STARTED: 'Hook Video Started',
  HOOK_VIDEO_PROGRESS: 'Hook Video Progress',
  GOLDEN_SCREEN_VIEWED: 'Golden Screen Viewed',
  EXPERIENCE_DETAIL_OPENED: 'Experience Detail Opened',
  EXPERIENCE_DETAIL_CONTINUED: 'Experience Detail Continued',
  BOOKING_OPTIONS_VIEWED: 'Booking Options Viewed',
  BOOKING_ACCOUNT_VIEWED: 'Booking Account Selection Viewed',
  RESERVATION_DETAILS_VIEWED: 'Reservation Details Viewed',
  RESERVATION_CONTACT_VIEWED: 'Reservation Contact Form Viewed',
  RESERVATION_SUMMARY_VIEWED: 'Reservation Summary Viewed',
  BOOKING_ACTION_SELECTED: 'Booking Action Selected',
  RESERVE_EXPERIENCE_CLICKED: 'Reserve Your Experience Clicked',
  SPEAK_TO_FOUNDER_CLICKED: 'Speak to the Founder Clicked',
  PRICE_VIEWED: 'Price Viewed',
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_VIEWED: 'Checkout Viewed',
  FUNNEL_STEP_ABANDONED: 'Funnel Step Abandoned',
  PAYMENT_SUCCEEDED: 'Payment Succeeded',
  FOUNDER_CONVERSATION_SCHEDULED: 'Founder Conversation Scheduled',
  GOOGLE_MEET_SCHEDULED: 'Google Meet Scheduled',
  PHONE_CALL_SCHEDULED: 'Phone Call Scheduled',
  LOGIN_PAGE_VIEWED: 'Login Page Viewed',
  LOGIN_ATTEMPTED: 'Login Attempted',
  LOGIN_SUCCEEDED: 'Login Succeeded',
  LOGIN_FAILED: 'Login Failed',
  SIGNUP_PAGE_VIEWED: 'Signup Page Viewed',
  SIGNUP_STARTED: 'Signup Started',
  SIGNUP_COMPLETED: 'Signup Completed',
  SIGNUP_FAILED: 'Signup Failed',
  INVITATION_LINK_OPENED: 'Invitation Link Opened',
  INVITATION_VERIFIED: 'Invitation Verified',
  INVITATION_INVALID: 'Invitation Invalid',
  INVITATION_ACCEPTED: 'Invitation Accepted',
  LANDING_PAGE_VIEWED: 'Landing Page Viewed',
  LANDING_PAGE_DURATION: 'Landing Page Duration',
  LANDING_CTA_CLICKED: 'Landing CTA Clicked',
  LANDING_SCROLL_DEPTH: 'Landing Scroll Depth',
  HEADER_NAVIGATION_CLICKED: 'Header Navigation Clicked',
  FOOTER_NAVIGATION_CLICKED: 'Footer Navigation Clicked',
  SEARCH_STARTED: 'Search Started',
  SEARCH_COMPLETED: 'Search Completed',
  SEARCH_FAILED: 'Search Failed',
  SEARCH_NO_RESULTS: 'Search Returned No Results',
  SEARCH_RESULTS_VIEWED: 'Search Results Viewed',
  LISTING_CARD_CLICKED: 'Listing Card Clicked',
  LISTING_BEEN_HERE_SELECTED: 'Been Here Selected',
  LISTING_WANT_TO_GO_SELECTED: 'Want To Go Selected',
  LISTING_TRAVEL_PREFERENCE_SAVE_FAILED: 'Listing Travel Preference Save Failed',
  PAYMENT_METHOD_SELECTED: 'Payment Method Selected',
  PAYMENT_ATTEMPTED: 'Payment Attempted',
  PAYMENT_FAILED: 'Payment Failed',
  CONVERSATION_SCHEDULING_VIEWED: 'Conversation Scheduling Viewed',
  CONVERSATION_METHOD_SELECTED: 'Conversation Method Selected',
  CONVERSATION_SCHEDULING_STARTED: 'Conversation Scheduling Started',
  CONVERSATION_SCHEDULING_FAILED: 'Conversation Scheduling Failed',
  PROFILE_SETTINGS_VIEWED: 'Profile Settings Viewed',
  PROFILE_UPDATE_STARTED: 'Profile Update Started',
  PROFILE_UPDATE_COMPLETED: 'Profile Update Completed',
  PROFILE_UPDATE_FAILED: 'Profile Update Failed',
  GOOGLE_CALENDAR_SETTINGS_VIEWED: 'Google Calendar Settings Viewed',
  GOOGLE_CALENDAR_CONNECT_STARTED: 'Google Calendar Connect Started',
  GOOGLE_CALENDAR_CONNECTED: 'Google Calendar Connected',
  GOOGLE_CALENDAR_CONNECT_FAILED: 'Google Calendar Connect Failed',
  GOOGLE_CALENDAR_DISCONNECT_STARTED: 'Google Calendar Disconnect Started',
  GOOGLE_CALENDAR_DISCONNECTED: 'Google Calendar Disconnected',
  GOOGLE_CALENDAR_DISCONNECT_FAILED: 'Google Calendar Disconnect Failed',
  PAYMENT_METHODS_VIEWED: 'Payment Methods Viewed',
  PAYMENT_METHOD_TAB_SELECTED: 'Payment Method Tab Selected',
  PAYMENT_METHOD_ADD_STARTED: 'Payment Method Add Started',
  PAYMENT_METHOD_ADDED: 'Payment Method Added',
  PAYMENT_METHOD_ADD_FAILED: 'Payment Method Add Failed',
  PAYMENT_METHOD_REMOVE_STARTED: 'Payment Method Remove Started',
  PAYMENT_METHOD_REMOVED: 'Payment Method Removed',
  PAYMENT_METHOD_REMOVE_FAILED: 'Payment Method Remove Failed',
  PASSWORD_SETTINGS_VIEWED: 'Password Settings Viewed',
  PASSWORD_CHANGE_STARTED: 'Password Change Started',
  PASSWORD_CHANGED: 'Password Changed',
  PASSWORD_CHANGE_FAILED: 'Password Change Failed',
  PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
  PASSWORD_RESET_EMAIL_SENT: 'Password Reset Email Sent',
  PASSWORD_RESET_REQUEST_FAILED: 'Password Reset Request Failed',
};

export const initializeMixpanel = () => {
  if (!isEnabled || initialized || typeof window === 'undefined' || !token) {
    return initialized;
  }

  mixpanel.init(token, {
    autocapture: false,
    track_pageview: false,
    persistence: 'localStorage',
    debug: process.env.REACT_APP_ENV === 'development',
  });
  initialized = true;
  return true;
};

export const trackMixpanelEvent = (eventName, properties = {}) => {
  if (!initializeMixpanel()) {
    return;
  }
  mixpanel.track(eventName, properties);
};

export const trackMixpanelPageView = (canonicalPath, previousPath) => {
  trackMixpanelEvent(MIXPANEL_EVENTS.PAGE_VIEWED, {
    path: canonicalPath,
    previous_path: previousPath || null,
  });
};

export const identifyMixpanelUser = currentUser => {
  if (!initializeMixpanel() || !currentUser?.id?.uuid) {
    return;
  }

  // console.log("identifyMixpanelUser, currentUser : ", currentUser);


  const { email, profile = {} } = currentUser.attributes || {};
  const { firstName, lastName, displayName, publicData = {} } = profile;
  const fullName =
    [firstName, lastName].filter(Boolean).join(' ') || displayName || undefined;
  const userId = currentUser.id.uuid;

  mixpanel.identify(userId);
  mixpanel.register({ sharetribe_user_id: userId, user_type: publicData.userType || null });
  mixpanel.people.set({
    $email: email,
    $name: fullName,
    $first_name: firstName,
    $last_name: lastName,
    sharetribe_user_id: userId,
    user_type: publicData.userType || null,
    is_guest: Boolean(publicData.isGuest),
  });

  // An event after identify links the anonymous device history to this account.
  mixpanel.track(MIXPANEL_EVENTS.USER_IDENTIFIED, {
    sharetribe_user_id: userId,
    user_type: publicData.userType || null,
  });
};

export const resetMixpanelUser = () => {
  if (!initializeMixpanel()) {
    return;
  }
  mixpanel.reset();
};

export const listingMixpanelProperties = listing => ({
  listing_id: listing?.id?.uuid || null,
  listing_title: listing?.attributes?.title || null,
  screen_experience: listing?.attributes?.publicData?.screen_experience || null,
});
